import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  DatePicker,
  StaticDatePicker,
  DateCalendar,
  TimePicker,
  StaticTimePicker,
  TimeClock,
  DateTimePicker,
  StaticDateTimePicker
} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { replaceKeywordsInText } from "../../component/Charts/dashboard.utils";

export default function DateTimeComponent({
  data,
  textValue,
  setTextValue,
  formaction
}) {
  const comp = data?.component;
  const sName = comp?.sName;
  const options = comp?.options;

  const dateformat = (date, sourceFormat = "YYYY-MM-DD", returnFormat) => {
    if (date) {
      const parseDate = (date, format) => {
        const parts = String(date)?.split("-");
        const formatParts = format?.toLowerCase()?.split("-");
        const dateObj = {};
        formatParts?.forEach((part, index) => {
          dateObj[part] = parseInt(parts[index], 10);
        });
        return new Date(dateObj.yyyy, dateObj.mm - 1, dateObj.dd);
      };

      const formatDate = (dateObj, format) => {
        const dd = String(dateObj.getDate()).padStart(2, "0");
        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
        const yyyy = dateObj.getFullYear();
        switch (format) {
          case "dd-mm-yyyy":
            return `${dd}-${mm}-${yyyy}`;
          case "yyyy-dd-mm":
            return `${yyyy}-${dd}-${mm}`;
          case "yyyy-mm-dd":
            return `${yyyy}-${mm}-${dd}`;
          case "mm-dd-yyyy":
            return `${mm}-${dd}-${yyyy}`;
          default:
            return "Invalid date format pattern";
        }
      };

      const parsedDate = parseDate(date, sourceFormat?.toLowerCase());
      if (isNaN(parsedDate)) {
        return "Invalid date";
      }
      return formatDate(parsedDate, returnFormat?.toLowerCase());
    } else {
      return "";
    }
  };

  // Resolve date keywords like :[startofmonth]:, :[today]:, etc.
  const resolveDate = (val) => {
    if (!val) return null;
    const resolved = replaceKeywordsInText(String(val));
    return resolved === "Invalid keyword" ? val : resolved;
  };

  const initialValue = resolveDate(comp?.sDefaultValue) || null;

  const [dateValue, setDateValue] = useState(
    initialValue ? dayjs(initialValue) : null
  );

  // Set initial textValue on mount
  useEffect(() => {
    if (sName && initialValue) {
      setTextValue(prev => ({
        ...prev,
        [sName]: dateformat(
          initialValue,
          comp?.sSourceFormat || "YYYY-MM-DD",
          comp?.sSendFormat
        )
      }));
    }
  }, []);

  // Sync dateValue when textValue changes externally (e.g. edit mode)
  useEffect(() => {
    const current = textValue?.[sName];
    if (current && current !== "NaN-NaN-NaN") {
      setDateValue(dayjs(current));
    }
  }, [textValue?.[sName]]);

  const handleChange = (event) => {
    if (!event) return;
    setDateValue(event);
    const formattedDate = dateformat(
      `${event.$y}-${String(event.$M + 1).padStart(2, "0")}-${String(event.$D).padStart(2, "0")}`,
      "YYYY-MM-DD",
      comp?.sSendFormat
    );
    setTextValue(prev => ({
      ...prev,
      [sName]: formattedDate
    }));
  };

  // Build props safely
  const formatProps = options?.others1 && typeof options.others1 === "object" ? options.others1 : {};
  const sProps = comp?.sProps && typeof comp.sProps === "object" ? comp.sProps : {};
  const { size, disabled, readOnly, ...restSProps } = sProps;

  const commonProps = {
    value: dateValue,
    onChange: handleChange,
    label: comp?.sLabel,
    disabled: disabled || false,
    readOnly: readOnly || false,
    ...formatProps,
    ...restSProps,
    slotProps: {
      textField: {
        helperText: comp?.sHelper,
        id: sName,
        size: size || "small"
      },
      popper: {
        disablePortal: false,
        placement: "bottom-start",
        style: { zIndex: 1300 }
      }
    }
  };

  const renderPicker = () => {
    const mode = options?.mode?.trim();
    const submode = options?.submode?.trim();

    if (mode === "DATE") {
      switch (submode) {
        case "StaticDatePicker":
          return <StaticDatePicker {...commonProps} />;
        case "DateCalendar":
          return <DateCalendar {...commonProps} />;
        case "DatePicker":
        default:
          return <DatePicker {...commonProps} />;
      }
    }

    if (mode === "TIME") {
      switch (submode) {
        case "StaticTimePicker":
          return <StaticTimePicker {...commonProps} />;
        case "TimeClock":
          return <TimeClock {...commonProps} defaultValue={dateValue} />;
        case "TimePicker":
        default:
          return <TimePicker {...commonProps} />;
      }
    }

    if (mode === "DATETIME") {
      switch (submode) {
        case "StaticDateTimePicker":
          return <StaticDateTimePicker {...commonProps} />;
        case "DateTimePicker":
        default:
          return <DateTimePicker {...commonProps} />;
      }
    }

    // Fallback to DatePicker
    return <DatePicker {...commonProps} />;
  };

  return (
    <Grid item {...data?.grid_props}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {renderPicker()}
      </LocalizationProvider>
    </Grid>
  );
}
